import {
  ShipmentHolds,
  ShipmentStatus,
  hasShipmentHold
} from '../../enums/loads.enum';
import I18N from '../../i18n/interface';

export function getShipmentStatusLabel(status: number): string {
  return ShipmentStatus[status] ?? String(status);
}

export type ShipmentStatusBadgeVariant =
  | 'pickup'
  | 'transit'
  | 'delivered'
  | 'cancelled'
  | 'hold'
  | 'default';

export interface IShipmentStatusBadge {
  label: string;
  variant: ShipmentStatusBadgeVariant;
}

type IStatusBadgeLocale = Pick<
  I18N,
  'WaitingForPickup' | 'InTransit' | 'Delivered' | 'Cancelled' | 'OnHold'
>;

// Groups the granular ShipmentStatus enum into the same three lifecycle buckets the
// Shipments grid's Pickup/InTransit/Delivered tabs already split rows into (plus
// Cancelled), so the Status column's badge reads and colors consistently regardless
// of which tab (or All) it's shown in.
//
// A movement hold is an overlay on the lifecycle status (holds bit flags, Bug 7178), not a
// status of its own, so it takes precedence over whatever bucket the status falls in. An
// invoice hold is internal and never shown to the customer. ShipmentStatus.Hold is retired
// (nothing writes it any more) but old data may still carry it, so it stays in the pickup bucket.
export function getShipmentStatusBadge(
  status: number,
  localeData: IStatusBadgeLocale,
  holds?: number | null
): IShipmentStatusBadge {
  if (hasShipmentHold(holds, ShipmentHolds.MovementHold)) {
    return { label: localeData.OnHold, variant: 'hold' };
  }
  switch (status as ShipmentStatus) {
    case ShipmentStatus.Created:
    case ShipmentStatus.ReadyForPickup:
    case ShipmentStatus.Hold:
    case ShipmentStatus.PickupStarted:
      return { label: localeData.WaitingForPickup, variant: 'pickup' };
    case ShipmentStatus.PickedUp:
    case ShipmentStatus.InTransit:
    case ShipmentStatus.DeliveryStarted:
      return { label: localeData.InTransit, variant: 'transit' };
    case ShipmentStatus.Delivered:
    case ShipmentStatus.ReadyToInvoice:
    case ShipmentStatus.OnInvoice:
    case ShipmentStatus.Closed:
      return { label: localeData.Delivered, variant: 'delivered' };
    case ShipmentStatus.Cancelled:
      return { label: localeData.Cancelled, variant: 'cancelled' };
    default:
      return { label: getShipmentStatusLabel(status), variant: 'default' };
  }
}
